/* Galaxy Lighting - sticky header, v7 (2026-10-21)

   v7: HIDE ON SCROLL DOWN, SHOW ON SCROLL UP (Johnson 2026-10-21).
   Header setting "Sticky header" = "On scroll up". The header is position:sticky
   from the very first frame (same layout as the old "Always" mode, so nothing
   jumps) and only ever moves with transform:
     - scroll down past the header  -> slides up out of view (ease-in)
     - scroll up                     -> slides back down (ease-out)
   html.gx-hdr-hidden mirrors the state so things pinned under the header
   (collection filters, product gallery, blog contents box) can glide up and
   down with it. See galaxy-header-motion.css.

   Dawn's own on-scroll-up mode lets the header scroll away in the page and
   then switches it to sticky+hidden in one frame. With a transform transition
   that frame makes the header flash back in at the top and slide out again,
   and base.css also moves `top`, which snaps. Neither happens here.

   EARLIER LESSONS (do not undo):
     - v3: never name fields this.dir etc. HTMLElement.dir is a native string
       property; every internal field is prefixed gx.
     - v3: .section-header can report offsetHeight 1; the inner .header is
       measured instead.
     - v5: trackpad momentum wobbles a few px backwards at the end of a flick.
       A reversal must cover FLIP_PX before it is believed.
     - v5: hand jitter is a flat +-4px oscillation. A gesture needs one real
       delta (PEAK) or a long, clearly deliberate travel before it counts.
     - v6: never add Dawn's .animate class (it swaps our transform transition
       for `top .15s`).

   GUARDS - the header never moves while:
     - a mega menu, the mobile drawer, the cart drawer or a modal is open
     - the search box (or anything else in the header) has focus, so the phone
       keyboard opening cannot push it away mid-typing
     - the page is in the rubber-band zone at the very bottom
   Tabbing into the header always brings it back. */
(function () {
  if (window.__gxSticky) return;
  window.__gxSticky = true;

  var NOISE = 2;          // ignore deltas smaller than this
  var PEAK_HIDE = 10;     // hiding: a gesture needs one delta this big...
  var LONG_HIDE = 160;    // ...or this much slow, steady travel
  var PEAK_SHOW = 5;      // showing is easier: any deliberate upward move
  var LONG_SHOW = 48;
  var HIDE_PX = 64;       // downward travel before hiding
  var SHOW_PX = 18;       // upward travel before revealing
  var FLIP_PX = 8;        // a reversal must cover this much before it is believed
  var TOP_ZONE = 80;      // keep the header while still this close past its own height
  var BOTTOM_ZONE = 90;   // never change state inside the rubber-band zone

  var root = document.documentElement;

  class GalaxyStickyHeader extends HTMLElement {
    connectedCallback() {
      this.gxHeader = this.closest('.section-header') || document.querySelector('.section-header');
      if (!this.gxHeader) return;

      var type = this.getAttribute('data-sticky-type');
      this.gxAlways = type === 'always' || type === 'reduce-logo-size';

      this.gxLastY = this.gxScrollY();
      this.gxDir = 0; this.gxTravel = 0; this.gxPeak = 0; this.gxPending = 0;
      this.gxHidden = false;
      this.gxHeight = 0;

      this.gxMeasure();
      /* Sticky from the first frame in every mode. */
      this.gxHeader.classList.add('shopify-section-header-sticky');
      this.gxHeader.classList.remove('shopify-section-header-hidden', 'animate');
      root.classList.remove('gx-hdr-hidden');

      this.gxOnScroll = this.gxScroll.bind(this);
      this.gxOnResize = this.gxMeasure.bind(this);
      this.gxOnFocus = this.gxReveal.bind(this);

      window.addEventListener('scroll', this.gxOnScroll, { passive: true });
      window.addEventListener('resize', this.gxOnResize, { passive: true });
      window.matchMedia('(max-width: 990px)').addEventListener('change', this.gxOnResize);
      this.gxHeader.addEventListener('focusin', this.gxOnFocus);

      /* Dawn's mega menus / media gallery dispatch this to stop the header
         reappearing. Deliberately ignored - scrolling up must always reveal. */
      this.addEventListener('preventHeaderReveal', function () {});
    }

    disconnectedCallback() {
      window.removeEventListener('scroll', this.gxOnScroll);
      window.removeEventListener('resize', this.gxOnResize);
      if (this.gxHeader) this.gxHeader.removeEventListener('focusin', this.gxOnFocus);
    }

    gxScrollY() {
      return window.pageYOffset || document.documentElement.scrollTop || 0;
    }

    gxMeasure() {
      if (!this.gxHeader) return;
      var inner = this.gxHeader.querySelector('.header');
      var h = this.gxHeader.offsetHeight;
      if (h < 20) h = (inner && inner.offsetHeight) || 0;
      if (h < 20) h = 120;
      this.gxHeight = h;
      root.style.setProperty('--header-height', h + 'px');
    }

    gxBusy() {
      var hd = this.gxHeader;
      if (!hd) return false;
      if (hd.querySelector('details[open]')) return true;
      if (document.querySelector('.menu-drawer-container details[open]')) return true;
      var bc = document.body.className;
      if (bc.indexOf('overflow-hidden') > -1) return true;
      var ae = document.activeElement;
      /* Typing in the header search. Only text fields: a clicked link or menu
         toggle keeps focus too, and that must not pin the header. */
      if (ae && hd.contains(ae) && /^(INPUT|TEXTAREA|SELECT)$/.test(ae.tagName)) return true;
      /* Search suggestions showing. Only the search input: Dawn's drawer
         "back" buttons carry aria-expanded="true" permanently. */
      if (hd.querySelector('input[aria-expanded="true"]')) return true;
      return false;
    }

    gxAtBottom(y) {
      var max = Math.max(0, document.documentElement.scrollHeight - window.innerHeight);
      return (max - y) <= BOTTOM_ZONE;
    }

    /* Handled straight in the scroll event (browsers already fire it at most
       once per frame). Not deferred to requestAnimationFrame: rAF is frozen in
       background / hidden tabs and embedded previews, where the header then
       silently never moved. The work per event is a few comparisons. */
    gxScroll() {
      this.gxUpdate();
    }

    gxNewGesture(d, mag) {
      this.gxDir = d; this.gxTravel = mag; this.gxPeak = mag; this.gxPending = 0;
    }

    gxUpdate() {
      if (!this.gxHeader) return;

      var y = this.gxScrollY();
      var dy = y - this.gxLastY;
      this.gxLastY = y;

      /* Back at the top: always shown. */
      if (y <= 0) {
        this.gxDir = 0; this.gxTravel = 0; this.gxPeak = 0; this.gxPending = 0;
        this.gxHeader.classList.remove('scrolled-past-header');
        this.gxReveal();
        return;
      }

      if (this.gxBusy()) return;
      if (this.gxAtBottom(y)) return;
      if (Math.abs(dy) < NOISE) return;

      var d = dy > 0 ? 1 : -1;
      var mag = Math.abs(dy);

      if (this.gxDir === 0) {
        this.gxNewGesture(d, mag);
      } else if (d === this.gxDir) {
        this.gxTravel += mag;
        if (mag > this.gxPeak) this.gxPeak = mag;
        this.gxPending = 0;
      } else {
        this.gxPending += mag;
        if (this.gxPending < FLIP_PX) return;
        this.gxNewGesture(d, this.gxPending);
      }

      if (y > this.gxHeight) this.gxHeader.classList.add('scrolled-past-header');

      if (this.gxDir < 0) {
        if (this.gxTravel >= SHOW_PX && (this.gxPeak >= PEAK_SHOW || this.gxTravel >= LONG_SHOW)) this.gxReveal();
      } else {
        if (this.gxTravel >= HIDE_PX && (this.gxPeak >= PEAK_HIDE || this.gxTravel >= LONG_HIDE)
            && y > this.gxHeight + TOP_ZONE) this.gxHide();
      }
    }

    gxHide() {
      if (this.gxAlways || this.gxHidden) return;
      this.gxHidden = true;
      this.gxHeader.classList.add('shopify-section-header-hidden');
      root.classList.add('gx-hdr-hidden');
      this.gxCloseMenus();
    }

    gxReveal() {
      if (!this.gxHidden) return;
      this.gxHidden = false;
      this.gxHeader.classList.remove('shopify-section-header-hidden');
      root.classList.remove('gx-hdr-hidden');
    }

    gxCloseMenus() {
      var d = this.gxHeader.querySelectorAll('header-menu');
      for (var i = 0; i < d.length; i++) { try { d[i].close(); } catch (e) {} }
      var m = this.gxHeader.querySelector('details-modal');
      if (m) { try { m.close(false); } catch (e) {} }
    }
  }

  if (!customElements.get('sticky-header')) {
    customElements.define('sticky-header', GalaxyStickyHeader);
  }

  /* Dawn's compiled bundle also defines 'sticky-header'. We got there first, so
     its call would throw NotSupportedError. Swallow that ONE name only. */
  var nativeDefine = customElements.define.bind(customElements);
  customElements.define = function (name, ctor, options) {
    if (name === 'sticky-header' && customElements.get(name)) return;
    return nativeDefine(name, ctor, options);
  };
})();
